import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import type { GridLineItem } from './salesOrderGridUtils'

export interface StatusReasonModalProps {
  isOpen: boolean
  line: GridLineItem | null
  targetStatus: 'draft' | 'sx' | 'cancelled'
  onClose: () => void
  onConfirm: (reason?: string) => void
}

export function StatusReasonModal({
  isOpen,
  line,
  targetStatus,
  onClose,
  onConfirm,
}: StatusReasonModalProps): React.ReactElement | null {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (!isOpen || !line) return null

  const isCancelling = targetStatus === 'cancelled'
  const isRestoring = line.line_status === 'cancelled' && targetStatus === 'draft'
  const isLeavingSx = line.line_status === 'sx' && isCancelling
  const requiresReason = isCancelling || isRestoring

  const title = isCancelling
    ? `Hủy dòng đơn hàng: ${line.variant_code || `#${line.tempKey}`}`
    : isRestoring
      ? `Khôi phục dòng về Nháp: ${line.variant_code || `#${line.tempKey}`}`
      : targetStatus === 'sx'
        ? `Chuyển dòng sang SX: ${line.variant_code || `#${line.tempKey}`}`
        : `Chuyển dòng về Nháp: ${line.variant_code || `#${line.tempKey}`}`

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (requiresReason && !reason.trim()) {
      setError('Vui lòng nhập lý do bắt buộc.')
      return
    }
    setError(null)
    onConfirm(reason.trim() || undefined)
  }

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-slate-900/60 p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3.5">
          <h3 className="font-bold text-slate-800 text-base">{title}</h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 text-sm space-y-4">
            {isLeavingSx && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 text-xs font-medium">
                ⚠️ <strong>Cảnh báo:</strong> Dòng đang ở Lệnh sản xuất, hủy sẽ gỡ khỏi Lệnh sản xuất.
              </div>
            )}

            {requiresReason ? (
              <div>
                <label className="block font-semibold text-slate-700 mb-1.5">
                  Lý do thực hiện <span className="text-rose-600">*</span>:
                </label>
                <textarea
                  className="w-full rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600"
                  rows={3}
                  placeholder="Nhập lý do chi tiết..."
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value)
                    if (error) setError(null)
                  }}
                  autoFocus
                />
                {error && <p className="mt-1 text-xs font-semibold text-rose-600">{error}</p>}
              </div>
            ) : (
              <p className="text-slate-600">
                Bạn có chắc chắn muốn chuyển dòng hàng này sang trạng thái{' '}
                <strong>{targetStatus === 'sx' ? 'Sản xuất (SX)' : 'Nháp'}</strong> không?
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            <Button variant="secondary" size="sm" type="button" onClick={onClose}>
              Đóng
            </Button>
            <Button
              variant={isCancelling ? 'danger' : 'primary'}
              size="sm"
              type="submit"
            >
              {isCancelling
                ? 'Xác nhận hủy dòng'
                : isRestoring
                  ? 'Khôi phục về Nháp'
                  : 'Xác nhận chuyển'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
